import { memo } from 'react';
import {
    Handle,
    NodeProps,
    HandleProps
} from 'reactflow';
import { IEdgeHandleIdKey } from './EdgeConfigurations';
import { formatAge } from '../utils/format';
import { IPatient } from '../caremap/ICareMap';
import { INodeData, getSupportGroupPositionByKey } from './NodeAdapter';
import { MemberNodeWrapper } from './MemberNodeWrapper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapDrawerRouter } from '../caremap/CareMapDrawerRouter';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../caremap/CareMapState';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';

type IHouseholdHandleProps = HandleProps & {
    id: IEdgeHandleIdKey,
    style?: object
}

function HouseholdHandle(props: IHouseholdHandleProps) {
    return <Handle {...props} />;
}

export const PatientNode = memo(({ data }: NodeProps<INodeData<IPatient>>) => {
    const drawerState = useInstance(CareMapDrawerState);
    const drawerRouter = useInstance(CareMapDrawerRouter);

    const careMapState = useInstance(CareMapState);
    const inWizard = !careMapState.state?.essentialMapComplete;
    useSubscription(() => careMapState);

    // can't click items in the wizard
    const onClick = inWizard ? undefined : () => {
        // in regular caremap, clicking a member goes to the list view;
        // in focused caremap, clicking a member goes to the member view
        const isFocused = drawerState.isOpen();
        const memberId = (isFocused ? 'patient' : undefined);

        drawerRouter.navigateToSelectedMemberId({ basePath: `/caremaps/${careMapState.state?.careMapId}/drawer/household`, memberId });
    };

    return (
        <>
            <>
                <HouseholdHandle
                    id="community-handle"
                    type="source"
                    {...getSupportGroupPositionByKey('community').patientHandle!}
                    isConnectable={false}
                />
                <HouseholdHandle
                    id="financial-handle"
                    type="source"
                    {...getSupportGroupPositionByKey('financial').patientHandle!}
                    isConnectable={false}
                />
                <HouseholdHandle
                    id="school-handle"
                    type="source"
                    {...getSupportGroupPositionByKey('school').patientHandle!}
                    isConnectable={false}
                />
                <HouseholdHandle
                    id="medical-handle"
                    type="source"
                    {...getSupportGroupPositionByKey('medical').patientHandle!}
                    isConnectable={false}
                />
                <HouseholdHandle
                    id="specialists-handle"
                    type="source"
                    {...getSupportGroupPositionByKey('specialists').patientHandle!}
                    isConnectable={false}
                />
            </>

            <MemberNodeWrapper
                onClick={onClick}
                size="large"
                label={data.label}
                subtext={data.dob ? formatAge(data.dob) : ''}
                photo={data.photoDataURI || data.photo}
                color={data.color}
                avatar={data.avatar}
                memberId="patient"
                selectedItemId={data.selectedItemId}
            />
        </>
    );
});
